'use client';

import { useEffect, useState } from 'react';

export function CountdownTile({ value, label }: { value: number; label: string }) {
  const [current, setCurrent] = useState(value);
  const [previous, setPrevious] = useState(value);
  const [flipKey, setFlipKey] = useState(0);

  useEffect(() => {
    if (value === current) return;
    setPrevious(current);
    setCurrent(value);
    setFlipKey((k) => k + 1);
  }, [value, current]);

  const currStr = String(current).padStart(2, '0');
  const prevStr = String(previous).padStart(2, '0');

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-20 w-16 [perspective:400px] sm:h-24 sm:w-20 md:h-28 md:w-24">
        {/* Top static — shows the NEW value's top half. Revealed when the
            old top flap rotates down out of the way. */}
        <Half position="top" digit={currStr} />
        {/* Bottom static — shows the OLD value's bottom half until the new
            bottom flap finishes rotating up to cover it. */}
        <Half position="bottom" digit={prevStr} />
        {flipKey > 0 && (
          <>
            {/* Top flap — shows OLD value, flips down (0 → -90deg) over 250ms. */}
            <Half
              key={`top-${flipKey}`}
              position="top"
              digit={prevStr}
              className="z-10 origin-bottom [animation:flip-down_250ms_ease-in_forwards]"
            />
            {/* Bottom flap — shows NEW value, flips up (90 → 0deg) starting at 250ms. */}
            <Half
              key={`bottom-${flipKey}`}
              position="bottom"
              digit={currStr}
              className="z-10 origin-top [animation:flip-up_250ms_ease-out_250ms_both]"
            />
          </>
        )}
        {/* Hairline split between the two halves */}
        <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 h-px -translate-y-1/2 bg-black/60" />
      </div>
      <span className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-dgd-gold/70 sm:text-xs">
        {label}
      </span>
    </div>
  );
}

function Half({
  position,
  digit,
  className = '',
}: {
  position: 'top' | 'bottom';
  digit: string;
  className?: string;
}) {
  const isTop = position === 'top';
  return (
    <div
      className={`absolute inset-x-0 ${
        isTop ? 'top-0 rounded-t-md border-b-0' : 'bottom-0 rounded-b-md border-t-0'
      } h-1/2 overflow-hidden border border-stone-700/60 bg-stone-900 shadow-inner [backface-visibility:hidden] ${className}`}
    >
      <div
        className={`absolute inset-x-0 flex h-[200%] items-center justify-center ${
          isTop ? 'top-0' : 'bottom-0'
        }`}
      >
        <span className="select-none text-4xl font-bold leading-none tabular-nums text-stone-100 sm:text-5xl md:text-6xl">
          {digit}
        </span>
      </div>
    </div>
  );
}
